import { Module, VuexModule, Mutation, Action, getModule } from 'vuex-module-decorators'
import Candle from '@/models/candle';
import axios from 'axios';
import store from '../store';

const UNLOADED_PAGE = -1;
const FINAL_PAGE = -2;

interface AddCandlesInput {
  page: number;
  candles: Candle[];
};

@Module({
  dynamic: true,
  name: 'candles',
  store,
})
class CandleModule extends VuexModule {

  // State
  /**
   * Whether or not candles are loading.
   */
  private static _loadingCandles = false;

  /**
   * Internal state for the new candle.
   */
  _newCandle?: Candle = undefined;

  /**
   * Loaded candles state.
   */
  _loadedCandles = {
    candles: [] as Candle[],
    page: UNLOADED_PAGE
  };

  _newCandleError?: { message?: string };

  // Accessors
  get newCandle() {
    return this._newCandle;
  }

  get newCandleError() {
    return this._newCandleError;
  }

  get candles() {
    return this._loadedCandles.candles;
  }

  get candlePage() {
    return this._loadedCandles.page;
  }

  get candlesLoaded() {
    return this._loadedCandles.page > UNLOADED_PAGE;
  }

  get allCandlesLoaded() {
    return this._loadedCandles.page === FINAL_PAGE;
  }

  get nextPage() {
    return this._loadedCandles.page + 1;
  }

  // MARK: Mutations
  @Mutation
  setCandle(candle: Candle) {
    this._newCandleError = undefined;
    this._newCandle = candle;
  }

  @Mutation
  setCandleError(newCandleError: {}) {
    console.log('New candle error: ', newCandleError);
    this._newCandleError = newCandleError;
  }

  @Mutation
  addCandles({ page, candles }: AddCandlesInput) {
    const { _loadedCandles = {
      candles: [] as Candle[]
    } } = this;

    if (candles.length === 0) {
      page = FINAL_PAGE;
    }

    this._loadedCandles = {
      page,
      candles: _loadedCandles.candles.concat(candles)
    };
  }

  // MARK: Actions
  /**
   * Loads candles if not already loaded.
   */
  @Action
  async loadCandles() {
    const { candlesLoaded } = this.context.getters;
    if (!candlesLoaded) {
      this.loadNextPage();
    }
  }

  /**
   * Create a new candle.
   */
  @Action
  async createCandle(newCandle: Candle) {
    try {
      const response = await axios.post('/api/v1/candles', newCandle, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      const candle = response.data;
      this.context.commit('setCandle', candle);
    } catch (e) {
      this.context.commit('setCandleError', e.response.data);
    }
  }

  @Action
  async loadNextPage() {
    if (CandleModule._loadingCandles) {
      return;
    }

    const { nextPage, allCandlesLoaded } = this.context.getters;
    let candles;

    if (allCandlesLoaded) {
      return;
    }

    try {
      CandleModule._loadingCandles = true;
      const response = await axios.get('/api/v1/candles', {
        params: {
          page: nextPage
        }
      });

      candles = response.data;
      this.context.commit('addCandles', { page: nextPage, candles });
    } catch (e) {
      console.log('Loading pages failed: ', e);
    }

    CandleModule._loadingCandles = false;
  }

}

export default getModule(CandleModule);
